import React, { useRef, useState } from 'react';
import _merge from 'lodash/merge';
import { getContext, useCombinedRefs } from 'common';
import KEY_CODES from 'common/KeyCodes';

import DeprecatedRippleEffect from '../RippleEffect/DeprecatedRippleEffect';
import ButtonCore from '../ButtonCore';
import Styled from './styles';

export interface ToggleGroupButtonProps extends Omit<React.ComponentPropsWithRef<'button'>, 'onChange'> {
  /**
   * onChange callback. It will return the event.
   * It will be called when user clicks the button or press key on the button.
   * User need to handle escape keydown specifically
   */
  onChange: (arg: React.MouseEvent | React.KeyboardEvent) => void;
  /** If it is true, the button is disable. */
  disabled?: boolean;
  /** If it is true, the button is selected. */
  isSelected?: boolean;
  /** If `true`, the button will be rendered without ripple effect. */
  disabledRipple?: boolean;
  /** Ripple style. */
  rippleStyle?: React.CSSProperties;
  /** Children prop is where button content resides. The content of the button including `ButtonText` and `ButtonIcon`. */
  children: React.ReactNode;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** To configure text wrapper element. It is used when only a string is passed as children. */
  buttonTextProps?: {};
  /**
   * @ignore
   * Size of the element, used internally by `SearchwChipsGlobalLogicButton` component
   * */
  size?: 'medium' | 'small';
  /** @ignore */
  onKeyDown?: React.KeyboardEventHandler;
  /** @ignore */
  onMouseDown?: React.MouseEventHandler;
}

const ToggleGroupButtonContext = React.createContext({});

const {
  ENTER_KEY,
  SPACE_KEY,
} = KEY_CODES;
const isSelectionKeyPressed = key => key && (key === ENTER_KEY || key === SPACE_KEY);
/**
 * Single ToggleGroupButton.
 * All additional props will be forwarded to the root element.
 */
const ToggleGroupButton = React.forwardRef<HTMLElement, ToggleGroupButtonProps>((props: ToggleGroupButtonProps, ref) => getContext(({ theme }) => {
  const {
    onChange,
    children,
    disabled,
    onKeyDown,
    onMouseDown,
    disabledRipple,
    rippleStyle,
    theme: themeProp,
    isSelected,
    size,
    ...otherProps
  } = props;

  const [isPressed, setIsPressed] = useState(false);
  const buttonWrapperRef = useRef(null);
  const combinedRef = useCombinedRefs(ref, buttonWrapperRef);
  const resolvedTheme = _merge({}, theme.TOGGLE_BUTTON_GROUP, themeProp);
  const resolvedProps = {
    isSelected,
    ...otherProps
  };

  const handleMouseDown = (e) => {
    onMouseDown && onMouseDown(e);
    !disabled && setIsPressed(true);
    !disabled && onChange(e);
  };

  const handleKeyDown = (e) => {
    if (isSelectionKeyPressed(e.key) && !disabled) {
      setIsPressed(true);
    }
    onKeyDown && onKeyDown(e);
    onChange(e);
  };

  const handleOnKeyUp = (event) => {
    const { onKeyUp } = otherProps;
    !disabled && setIsPressed(false);
    onKeyUp && onKeyUp(event);
  };

  const handleMouseUp = (event) => {
    const { onMouseUp } = otherProps;
    !disabled && setIsPressed(false);
    onMouseUp && onMouseUp(event);
  };
  const handleMouseLeave = (event) => {
    const { onMouseLeave } = otherProps;
    !disabled && setIsPressed(false);
    onMouseLeave && onMouseLeave(event);
  };
  const handleBlur = (event) => {
    const { onBlur } = otherProps;
    !disabled && setIsPressed(false);
    onBlur && onBlur(event);
  };

  return (
    <DeprecatedRippleEffect
      disabled={disabledRipple || disabled}
      rippleStyle={{
        backgroundColor: resolvedTheme.rippleColor,
      }}
      style={{
        ...rippleStyle
      }}
    >
      <ButtonCore
        ref={combinedRef}
        onMouseDown={handleMouseDown}
        onKeyDown={handleKeyDown}
        $theme={resolvedTheme}
        theme={resolvedTheme}
        $isPressed={isPressed}
        Wrapper={size === 'medium' ? Styled.ToggleButtonContent : Styled.SmallToggleButtonContent}
        disabled={disabled}
        aria-pressed={isSelected}
        isSelected={isSelected}
        {...resolvedProps}
        onKeyUp={handleOnKeyUp}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onBlur={handleBlur}
      >
        <ToggleGroupButtonContext.Provider value={{ isSelected, disabled, isPressed }} >
          {children}
        </ToggleGroupButtonContext.Provider>
      </ButtonCore>
    </DeprecatedRippleEffect>
  );
}));

ToggleGroupButton.defaultProps = {
  disabled: false,
  isSelected: false,
  disabledRipple: false,
  rippleStyle: {},
  theme: {},
  buttonTextProps: {},
  size: 'medium',
  onKeyDown: undefined,
  onMouseDown: undefined,
};

ToggleGroupButton.displayName = 'ToggleGroupButton';

export { ToggleGroupButton as default, ToggleGroupButtonContext };
